import { AuthOptions } from '@newfront-insurance/ms-graph-auth';
import { useEffect, useState } from 'react';

import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { useMSGraphAuth } from '../contexts/ms-graph-context';

/**
 * Side-effect component that fetches the MS Graph token when Outlook Connect is enabled
 * and writes it to additionalContext, following the same pattern as OutlookChatPayload.
 *
 * Only active when:
 * - outlookConnectEnabled is true (user toggled it on in settings)
 * - useMSGraphAuth() context is available (MSGraphRoot is enabled)
 * - authState is LOGGED_IN
 * - graphClient is non-null (MSAL initialized after page reload)
 *
 * This component doesn't render anything — it only manages context.
 */
export function NavigatorGraphPayload() {
  const { outlookConnectEnabled, setAdditionalContext } = useChatOverlayContext();
  const msGraphAuth = useMSGraphAuth();
  const [graphToken, setGraphToken] = useState<string | null>(null);

  const graphClient = msGraphAuth?.graphClient ?? null;
  const isLoggedIn = msGraphAuth?.authState.state === AuthOptions.LOGGED_IN;
  const getAccessToken = msGraphAuth?.getAccessToken ?? null;

  useEffect(() => {
    if (!outlookConnectEnabled || !isLoggedIn || !getAccessToken) {
      setGraphToken(null);
      return;
    }

    let isMounted = true;

    const resolveToken = async () => {
      try {
        const token = await getAccessToken();
        if (isMounted) {
          setGraphToken(token);
        }
      } catch (error) {
        console.error('[NavigatorGraphPayload] Failed to get graph token:', error);
        if (isMounted) {
          setGraphToken(null);
        }
      }
    };

    resolveToken();

    return () => {
      isMounted = false;
    };
    // graphClient becomes non-null after MSAL finishes initializing on page reload.
    // Including it ensures we retry after the restored login state is ready.
  }, [outlookConnectEnabled, isLoggedIn, getAccessToken, graphClient]);

  useEffect(() => {
    setAdditionalContext((prev) => {
      if (!graphToken) {
        // Remove userGraphToken from context when not available
        const { userGraphToken: _, ...rest } = prev as Record<string, unknown> & { userGraphToken?: string };
        return rest;
      }
      return { ...prev, userGraphToken: graphToken };
    });
  }, [setAdditionalContext, graphToken]);

  return null;
}
